import React, { useEffect, useState, useRef } from 'react';
import Link from 'components/Link';
import { get_bid_message } from 'reducers/AuctionReducer';
import { Loading } from 'components/Loading';
import { general_error_toastr, general_toastr_message, general_success_toastr } from 'components/partials/Notification';
import { trigger_cancel } from 'services/BaseService';
import ReactGA from 'react-ga4';
import AuctionDetailsSchema from 'components/Schemas/AuctionDetailsSchema';
import { cityStateToUrl, getRestrictionMessage } from 'helpers/auctions';
import AuctionTitle from 'components/Auctions/Details/AuctionTitle';
import AuctionBadge from 'components/Auctions/Details/AuctionBadge';
import AuctionInformation from 'components/Auctions/Details/AuctionInformation';
import { AmountSection } from 'components/Auctions/Details/AmountSection';
import StartsCloses from 'components/Auctions/Details/StartsCloses';
import SoftClose from 'components/Auctions/Details/SoftClose';
import AuctionExpires from 'components/Auctions/Details/AuctionExpires';
import AuctionCategories from 'components/Auctions/Details/AuctionCategories';
import AuctionBidBox from 'components/Auctions/Details/AuctionBidBox';
import AuctionDetails from 'components/Auctions/Details/AuctionDetails';
import ModalBidConfirmed from 'components/Auctions/Details/ModalBidConfirmed';
import { useRouter } from 'next/router';
import TimerProvider from 'context/TickContext';
import moment from 'moment';
import { get_utc_datetime } from 'helpers/date';
import ImageCarousel from './ImageCarousel';
import ManageMembershipModal from '../../ManageMembershipModal';
import { ModalNotification } from './ModalNotification';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import { useService } from 'hooks/useService';
import { useGlobalContext } from 'context/GlobalContext';
import { usePage } from 'context/PageContext';
import { useAuctionNavigation } from 'context/AuctionNavigationContext';
import Cookies from 'js-cookie';
import { useBidNotification } from 'context/BidNotificationContext';
import 'styles/Auctions/Detail.scss';
import 'styles/components/auction-tile.less';
import 'styles/components/auction-bidding.less';

export const Details = (props) => {
    const {
        userIp,
        auctionId,
        handleShowOtherAuctions,
    } = props;

    const [{ data: auctionData, loading: auctionLoading }, fetchAuction] = useService({
        url: `auctions/${auctionId}`,
        defaultData: { auction: {}, user: {} },
        hasAuthedUrl: true,
    });

    const [{}, toggleWatchService] = useService({ method: 'POST' });

    const [{}, submitBidService] = useService({
        url: 'users/account/auction-bids',
        method: 'POST',
    });

    const auction = auctionData?.auction || {};

    const {
        status_id: statusId,
        facility,
        images = [],
        city,
        current_date: currentDate,
    } = auction;

    const router = useRouter();
    const globalContext = useGlobalContext();
    const { navigatingToAuction, setNavigatingToAuction } = useAuctionNavigation();
    const { handleBidBanner } = useBidNotification();
    const { resetPage } = usePage();
    const contextUser = globalContext.user;

    const [user, setUser] = useState({});
    const [notification, setNotification] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [showManageMembershipModal, setShowManageMembershipModal] = useState(false);
    const [bidConfirmed, setBidConfirmed] = useState(false);
    const [typedBidAmount, setTypedBidAmount] = useState('');
    const [continuePolling, setContinuePolling] = useState(true);
    const [auctionWatchLoading, setAuctionWatchLoading] = useState(false);
    const [isPlacingBid, setIsPlacingBid] = useState(false);
    const [isPlacingBidModal, setIsPlacingBidModal] = useState(false);
    const [submittingBid, setSubmittingBid] = useState(false);
    const [intervalTick, setIntervalTick] = useState(5000);
    const [processing, setProcessing] = useState(true);
    const [resetBidValue, setResetBidValue] = useState(false);
    const auctionIdRef = useRef(auctionId);
    const [initialLoadComplete, setInitialLoadComplete] = useState(false);

    const timeoutRef = useRef(null);

    const fireGetAuction = async () => {
        try {
            const data = await fetchAuction({
                params: {
                    user_ip: userIp,
                    ignore_redirect: false,
                },
            });
            
            if (data.auction.redirect === true) {
                const cityStateUrl = cityStateToUrl({
                    state: data.auction.state, 
                    city: data.auction.city,
                });

                window.localStorage.setItem('search_obj',
                    JSON.stringify({
                        type: 'city',
                        term: data.auction.facility.city,
                    }),
                );
                router.push(`/auctions/${cityStateUrl}`);
            }
            
            setUser(data.user);
            setInitialLoadComplete(true);
            
            startTickInterval();
            handleShowOtherAuctions(true);
            setNavigatingToAuction(false);
        } catch (error) {
            console.error('Error fetching auction:', error);
            setInitialLoadComplete(true);
        }
    };

    useEffect(() => {
        auctionIdRef.current = auctionId;
        fireGetAuction();

        return () => {
            clearTimeoutRef();
        };
    }, [auctionId]);

    useEffect(() => {
        const handlePopState = async () => {
            router.push(window.location.href);
        };
        window.addEventListener('popstate', handlePopState);

        return () => {
            window.removeEventListener('popstate', handlePopState);
            clearTimeoutRef();
            setContinuePolling(false);
            trigger_cancel('auctions.getComingSoonAuctions');
            trigger_cancel('auctions.getAuction');
            handleShowOtherAuctions(false);
            handleBidBanner();
            resetPage();
        };
    }, []);

    const tick = () => {
        handleIntervalTick();

        // Set the new timeout for the next tick
        timeoutRef.current = setTimeout(tick, intervalTick);
    };

    const startTickInterval = () => {
        clearTimeoutRef();
        tick();
    };

    const clearTimeoutRef = () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    };

    const handleIntervalTick = () => {
        return new Promise((resolve) => {
            getData(auctionIdRef.current, true)
                .then(({ user: { is_bidder = false, is_high_bidder = false } = {} } = {}) => {
                    if (is_bidder && is_high_bidder && submittingBid === true) {
                        setSubmittingBid(false);
                        bidSubmittedAndApiRegistered({ slug: false, status: true });
                    }
                })
                .finally(() => {
                    resolve();
                });
        });
    };

    const getData = (auction_id, refresh = false) => {
        return new Promise((resolve) => {
            if (!continuePolling) {
                resolve({});
                return;
            }

            fetchAuction({
                url: `auctions/${auction_id}`,
                params: {
                    refresh,
                },
            })
                .then((data) => {
                    setUser(data.user);
                    setProcessing(false);
                    const { status_id: status } = data.auction;

                    // Active auction
                    if (status === '1') {
                        // If bidder show a notification for them
                        if (data.auction.user.is_bidder) {
                            if (data.auction.user.is_high_bidder) {
                                const notification = get_bid_message('bid-high-bidder');
                                handleBidBanner(notification);
                            } else {
                                const notification = get_bid_message('outbid');
                                handleBidBanner(notification);
                            }
                        } else {
                            handleBidBanner();
                        }
                    } else if (status === '2' && data.auction.user.is_high_bidder === true) {
                        // If bidder show a notification for them
                        const notification = get_bid_message('auction-winner');
                        handleBidBanner(notification);
                    }
    
                    resolve(data.auction);
                })
                .catch((error) => {
                    const response = error.response || { status: 0 };
                    if (response.status === 204) {
                        // 204 - canceled
                        return true;
                    }
                    if (response.status === 404) {
                        router.push('/404');
                        return true;
                    }
                    general_error_toastr();
                    router.push('/auctions');
                    resolve();
                });
        });
    };

    const bidSubmittedAndApiRegistered = ({ reasons = [{slug: 'user-restriction-won-auctions'}], status }) => {
        // Render a notification if there is one. This will show on the top of the page and in the modal
        if (reasons.length) {
            const reason = getRestrictionMessage(reasons);
            setNotification(reason);
        } else {
            closeConfirmModal();
        }

        // Bid was saved in the api. Everything looks good.
        if (status === true) {
            // Bid has been submitted. Show the toastr.
            setResetBidValue(true);
            const foundBidSuccessMessage = get_bid_message('bid-success', false);
            showToastr(foundBidSuccessMessage);

            // Reset these values they are no longer needed.
            setBidConfirmed(false);
            setTypedBidAmount('');

            // All is good close the modal
            closeConfirmModal();
            getData(auctionIdRef.current, true);
            window.scrollTo(0, 0);
        }
    };

    const showToastr = ({type, body, subject}) => {
        general_toastr_message({title: subject, message: body, type});
    };

    const openConfirmModal = () => {
        setResetBidValue(false);
        setShowConfirmModal(true);
        setIsPlacingBid(true);
    };

    const confirmModalClosed = () => {
        setShowConfirmModal(false);
        setIsPlacingBid(false);
        setIsPlacingBidModal(false);
    };

    const closeConfirmModal = () => {
        confirmModalClosed();
    };

    const modalBidConfirmed = () => {
        setNotification(false);
        
        // If the auction has expired after the bid has been submitted then display a modal for this message
        if (statusId !== '1') {
            const notification = get_bid_message('auction-expired', false);
            setNotification(notification);
            openConfirmModal();
            return false;
        }
        
        setSubmittingBid(true);
        setIsPlacingBidModal(true);
        
        const ga_session_id = Cookies.get('_ga_YM2Y56PMJX');
        
        submitBidService({
            data: { 
                auction_id: auctionIdRef.current, 
                max_bid: bidConfirmed,
                ga_session_id, 
            },
        })
            .then(response => {
                setSubmittingBid(false);
                bidSubmittedAndApiRegistered({ 
                    reasons: response.reasons || [], 
                    status: response.status,
                });
                
                ReactGA.event({
                    category: 'User',
                    action: 'User Bid',
                    value: parseFloat(bidConfirmed),
                });
                
                window.dataLayer.push({
                    event: 'bid',
                    bidAmount: bidConfirmed,
                });
            })
            .catch(error => {
                console.error('Error entering bid', error);
                setSubmittingBid(false);
                closeConfirmModal();
                getData(auctionIdRef.current);
                general_error_toastr();
            });
    };

    const toggleAuctionWatch = () => {
        const { watchlist = false } = auction.user;
        const addWatch = !watchlist;
        setAuctionWatchLoading(true);
        
        toggleWatchService({
            url: 'users/account/watchlist',
            method: addWatch ? 'POST' : 'DELETE',
            params: { auction_id: auctionIdRef.current },
        })
            .then(() => {
                return getData(auctionIdRef.current, true);
            })
            .then(() => {
                setAuctionWatchLoading(false);
                general_success_toastr({message: 'Your AuctionWatch list has successfully been updated', title: 'Success!'});
            })
            .catch((error) => {
                console.error('Error toggling auction watch:', error);
                setAuctionWatchLoading(false);
                general_error_toastr();
            });
    };

    const handleTypedBidAmount = (typedBidAmount) => {
        setTypedBidAmount(typedBidAmount);
    };

    const handleNotificationState = (notification) => {
        setNotification(notification);
    };

    const handleBidConfirmed = (intNewUserMaxBid) => {
        setBidConfirmed(intNewUserMaxBid);
        setNotification(false);
        openConfirmModal();
    };

    if (!auctionId || navigatingToAuction || (!initialLoadComplete && auctionLoading)) {
        return (
            <Loading/>
        );
    }

    const canBid = auction?.user?.can_bid;

    return auction.redirect != true ? (
        <TimerProvider currentDate={parseInt(moment(get_utc_datetime(currentDate)) + '')}>
            {canBid?.status === false && canBid.reasons?.length > 0 && (
                <div className="container-fluid">
                    <div className="alert alert-danger text-left">
                        <ModalNotification
                            notification={{...getRestrictionMessage(canBid?.reasons), type: false}}
                            close_confirm_modal={closeConfirmModal}
                            openMembershipModal={() => {
                                closeConfirmModal();
                                setShowManageMembershipModal(true);
                            }}
                        />
                    </div>
                </div>)
            }
            <div className="auction-detail">
                <AuctionDetailsSchema auction={auction} />
                <Row>
                    <Col md={12} lg={5}>
                        <AuctionTitle
                            authed={contextUser.authed}
                            regType={user?.reg_type}
                            auction={auction}
                            auctionwatch_loading={auctionWatchLoading}
                            auction_id={auctionIdRef.current}
                            getData={getData}
                            toggleAuctionWatch={toggleAuctionWatch}
                        />

                        <div className="text-center">
                            {!processing ? (
                                <ImageCarousel images={images} city={city} />
                            ) : null}
                        </div>
                    </Col>
                    <Col md={12} lg={7}>
                        <div className="details-container">
                            <AuctionBadge auction={auction} />

                            <Link
                                to={`/facilities/${facility?.facility_id}`}
                                className="auction-details-fac-name"
                            >
                                <i className="fa fa-external-link-square"/>
                                {' '}
                                {facility?.facility_name}
                            </Link>

                            <AuctionInformation
                                auction={auction}
                                facility={facility}
                            />

                            <div className="flex-container clock-container clock-container-wrapper text-center amount-section">
                                <AmountSection auction={auction} />
                                <StartsCloses auction={auction} />
                            </div>
                            <SoftClose auction={auction}/>
                            <div className="clear"/>
                            <div className="col-md-12 marginT15 text-center">
                                <AuctionExpires auction={auction}/>
                            </div>
                            <AuctionCategories auction={auction} />
                            <div className="row">
                                <AuctionBidBox
                                    auctionDetailsPage={true}
                                    auction={auction}
                                    auctionId={auctionIdRef.current}
                                    isPlacingBid={isPlacingBid}
                                    typedBidAmount={typedBidAmount}
                                    handleTypedBidAmount={handleTypedBidAmount}
                                    showToastr={showToastr}
                                    toggleAuctionWatch={toggleAuctionWatch}
                                    handleNotificationState={handleNotificationState}
                                    openConfirmModal={openConfirmModal}
                                    handleBidConfirmed={handleBidConfirmed}
                                    resetBidValue={resetBidValue}
                                    canBid={canBid?.status}
                                />
                            </div>
                        </div>
                    </Col>
                </Row>
                <AuctionDetails auction={auction} />
                <ModalBidConfirmed
                    show={showConfirmModal}
                    isPlacingBidModal={isPlacingBidModal}
                    notification={notification}
                    close_confirm_modal={closeConfirmModal}
                    confirmModalClosed={closeConfirmModal}
                    bidConfirmed={bidConfirmed}
                    auction={auction}
                    modalBidConfirmed={modalBidConfirmed}
                    premiumPercentage={user?.membership?.premium_percentage}
                    openMembershipModal={() => {
                        closeConfirmModal();
                        setShowManageMembershipModal(true);
                    }}
                    minimumBid={auction?.total_bids == 0 ? auction?.initial_bid?.amount : auction?.minimum_bid?.amount}
                />
            </div>
            <ManageMembershipModal
                membership_type={user?.membership?.type}
                show={showManageMembershipModal}
                onHide={() => setShowManageMembershipModal(false)}
                user_id={user?.user_id}
                payment_method={user?.payment_method}
                handleMembershipSubmitSuccess={() => console.log('submit')}
            />
        </TimerProvider>
    ) : null;
};